import './cartFooter.scss';

import { FormattedMessage } from 'react-intl';
import { Button, ButtonColor, ButtonVariant, Size, Skeleton } from '@eliberty/design-system-react';

import { FormattedCurrency } from '~atoms';

export interface CartFooterProps {
  totalInCents: number;
  currency: string;
  isPriceLoading?: boolean;
  hasError?: boolean;
  onClear: () => void;
  onCheckout: () => void;
}

const LockIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='12'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    viewBox='0 0 24 24'
    width='12'
  >
    <rect height='11' rx='2' width='16' x='4' y='11' />
    <path d='M8 11V7a4 4 0 118 0v4' />
  </svg>
);

const CheckIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='12'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2.2'
    viewBox='0 0 24 24'
    width='12'
  >
    <path d='M5 12l5 5 9-11' />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='18'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    viewBox='0 0 24 24'
    width='18'
  >
    <path d='M5 12h14M13 6l6 6-6 6' />
  </svg>
);

const CartFooter = ({
  currency,
  hasError = false,
  isPriceLoading = false,
  onCheckout,
  onClear,
  totalInCents,
}: CartFooterProps) => {
  let totalContent;
  if (isPriceLoading) {
    totalContent = <Skeleton height={28} width={96} />;
  } else if (hasError) {
    totalContent = <span aria-hidden>—</span>;
  } else {
    totalContent = <FormattedCurrency currency={currency} value={totalInCents} />;
  }

  return (
    <div className='CartFooter'>
      <div className='CartFooter_total'>
        <div className='CartFooter_totalLabelGroup'>
          <span className='CartFooter_totalLabel'>
            <FormattedMessage defaultMessage='Total' id='cartTotal' />
          </span>
          <span className='CartFooter_totalSub'>
            <FormattedMessage defaultMessage='Incl. VAT' id='cartTotalTaxSub' />
          </span>
        </div>
        <span className='CartFooter_totalValue'>{totalContent}</span>
      </div>
      <div className='CartFooter_secondary'>
        <button className='CartFooter_clearButton' onClick={onClear} type='button'>
          <FormattedMessage defaultMessage='Empty cart' id='cartActionClear' />
        </button>
      </div>
      <Button
        color={ButtonColor.Primary}
        disabled={hasError}
        onClick={onCheckout}
        size={Size.L}
        variant={ButtonVariant.Filled}
      >
        <FormattedMessage defaultMessage='Checkout' id='cartActionCheckout' />
        <ArrowRightIcon />
      </Button>
      <div className='CartFooter_trust'>
        <span className='CartFooter_trustItem'>
          <LockIcon />
          <FormattedMessage defaultMessage='Secure payment' id='cartTrustSecure' />
        </span>
        <span aria-hidden className='CartFooter_trustSep'>
          ·
        </span>
        <span className='CartFooter_trustItem'>
          <CheckIcon />
          <FormattedMessage defaultMessage='Flexible cancellation' id='cartTrustFlexible' />
        </span>
      </div>
    </div>
  );
};

export default CartFooter;
